/**
 * Per-mine site config. Fetched from /site.json (public/) before first render.
 * Site copies (EU, PK, SC, …) override this via public/site.json.
 */

export const DEFAULT_SITE = {
  productName: 'MineDynamicsViewer',
  projectName: 'MineDynamics',
  logoUrl: null,
  origin: {
    east: 0,
    north: 0,
    rl: 0,
  },
  theme: {
    gold: '#eab308',
    goldDim: '#a16207',
    black: '#0a0a0a',
  },
  modules: {
    openPit: false,
    underground: false,
  },
  defaultLayers: [],
  defaultCamera: {
    position: [0, 1500, 2000],
    lookAt: [0, 0, 0],
  },
  /** UG level sprites. offsetEast/offsetNorth are metres from origin; levels are [name, rl]. */
  levelLabels: {
    offsetEast: 0,
    offsetNorth: 0,
    levels: [],
  },
  /** Named section jumps. bounds are [min, max] mine coords; omit to derive from origin. */
  sections: {
    bounds: { east: null, north: null, rl: null },
    zones: [],
    benches: [],
    northings: [],
  },
  /** tblSYSDataSet.Project values allowed for hole queries. Empty = no project filter. */
  holeProjects: [],
  /**
   * Assay / block-model colour bins used on first load (and on legend Reset).
   * Last row with no `min` is the floor ("below") bin. Override in site.json.
   */
  gradeBins: [
    { min: 3, color: 0xac02b4 },
    { min: 1.5, color: 0xff0000 },
    { min: 0.6, color: 0x00ff00 },
    { min: Number.NEGATIVE_INFINITY, color: 0x0000ff },
  ],
};

let site = {
  ...DEFAULT_SITE,
  origin: { ...DEFAULT_SITE.origin },
  modules: { ...DEFAULT_SITE.modules },
  levelLabels: {
    ...DEFAULT_SITE.levelLabels,
    levels: [...DEFAULT_SITE.levelLabels.levels],
  },
  sections: emptySections(),
  holeProjects: [...DEFAULT_SITE.holeProjects],
  gradeBins: cloneGradeBins(DEFAULT_SITE.gradeBins),
};

function mergeModules(raw) {
  const incoming = raw && typeof raw === 'object' ? raw : {};
  return {
    openPit: incoming.openPit === true,
    underground: incoming.underground === true,
  };
}

function emptySections() {
  return {
    bounds: { east: null, north: null, rl: null },
    zones: [],
    benches: [],
    northings: [],
  };
}

function namedCoordList(list, key) {
  if (!Array.isArray(list)) return [];
  return list
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      const name = String(row.name || '').trim();
      const value = Number(row[key] ?? row.rl ?? row.east ?? row.north);
      if (!name || !Number.isFinite(value)) return null;
      return { name, [key]: value };
    })
    .filter(Boolean);
}

function boundsPair(value) {
  if (!Array.isArray(value) || value.length < 2) return null;
  const a = Number(value[0]);
  const b = Number(value[1]);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return a <= b ? [a, b] : [b, a];
}

function mergeSections(raw) {
  const incoming = raw && typeof raw === 'object' ? raw : {};
  const boundsIn = incoming.bounds && typeof incoming.bounds === 'object' ? incoming.bounds : {};
  return {
    bounds: {
      east: boundsPair(boundsIn.east),
      north: boundsPair(boundsIn.north),
      rl: boundsPair(boundsIn.rl),
    },
    zones: namedCoordList(incoming.zones, 'east'),
    benches: namedCoordList(incoming.benches, 'rl'),
    northings: namedCoordList(incoming.northings, 'north'),
  };
}

function mergeLevelLabels(raw) {
  const incoming = raw && typeof raw === 'object' ? raw : {};
  const offsetEast = Number(incoming.offsetEast);
  const offsetNorth = Number(incoming.offsetNorth);
  const levels = Array.isArray(incoming.levels)
    ? incoming.levels
        .map((row) => {
          if (!Array.isArray(row) || row.length < 2) return null;
          const rl = Number(row[1]);
          if (!Number.isFinite(rl)) return null;
          return [String(row[0]), rl];
        })
        .filter(Boolean)
    : [];
  return {
    offsetEast: Number.isFinite(offsetEast) ? offsetEast : DEFAULT_SITE.levelLabels.offsetEast,
    offsetNorth: Number.isFinite(offsetNorth) ? offsetNorth : DEFAULT_SITE.levelLabels.offsetNorth,
    levels,
  };
}

function mergeHoleProjects(raw) {
  if (!Array.isArray(raw)) return [...DEFAULT_SITE.holeProjects];
  const seen = new Set();
  const out = [];
  raw.forEach((item) => {
    const value = String(item || '').trim();
    if (!value || seen.has(value)) return;
    seen.add(value);
    out.push(value);
  });
  return out;
}

function cloneGradeBins(list) {
  return (Array.isArray(list) ? list : []).map((row) => ({
    min: row.min,
    color: row.color,
    hidden: !!row.hidden,
  }));
}

function parseGradeBinColor(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(0, Math.min(0xffffff, Math.trunc(value)));
  }
  const raw = String(value || '').replace(/^#/, '').trim();
  if (!raw) return null;
  const n = parseInt(raw, 16);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.min(0xffffff, n));
}

/** site.json rows: `{ min, color }` with hex strings; omit `min` for the floor bin. */
function mergeGradeBins(raw) {
  if (!Array.isArray(raw) || !raw.length) {
    return cloneGradeBins(DEFAULT_SITE.gradeBins);
  }
  const fallbackFloor = DEFAULT_SITE.gradeBins[DEFAULT_SITE.gradeBins.length - 1];
  let floorColor = fallbackFloor.color;
  let floorHidden = false;
  const finite = [];
  const seen = new Set();
  raw.forEach((row) => {
    if (!row || typeof row !== 'object') return;
    const color = parseGradeBinColor(row.color);
    if (color == null) return;
    const minRaw = row.min;
    const hidden = !!row.hidden;
    const isFloor =
      minRaw == null ||
      minRaw === '' ||
      minRaw === '-Infinity' ||
      String(minRaw).toLowerCase() === 'below';
    const min = isFloor ? NaN : Number(minRaw);
    if (!Number.isFinite(min)) {
      floorColor = color;
      floorHidden = hidden;
      return;
    }
    if (seen.has(min)) return;
    seen.add(min);
    finite.push({ min, color, hidden });
  });
  if (!finite.length) {
    return cloneGradeBins(DEFAULT_SITE.gradeBins);
  }
  finite.sort((a, b) => b.min - a.min);
  return [...finite, { min: Number.NEGATIVE_INFINITY, color: floorColor, hidden: floorHidden }];
}

function mergeSite(raw) {
  if (!raw || typeof raw !== 'object') {
    return {
      ...DEFAULT_SITE,
      origin: { ...DEFAULT_SITE.origin },
      theme: { ...DEFAULT_SITE.theme },
      modules: { ...DEFAULT_SITE.modules },
      levelLabels: {
        ...DEFAULT_SITE.levelLabels,
        levels: [...DEFAULT_SITE.levelLabels.levels],
      },
      sections: emptySections(),
      holeProjects: [...DEFAULT_SITE.holeProjects],
      gradeBins: cloneGradeBins(DEFAULT_SITE.gradeBins),
    };
  }
  const origin = { ...DEFAULT_SITE.origin, ...(raw.origin || {}) };
  const theme = { ...DEFAULT_SITE.theme, ...(raw.theme || {}) };
  const east = Number(origin.east);
  const north = Number(origin.north);
  const rl = Number(origin.rl);
  return {
    ...DEFAULT_SITE,
    ...raw,
    logoUrl: raw.logoUrl || null,
    origin: {
      east: Number.isFinite(east) ? east : DEFAULT_SITE.origin.east,
      north: Number.isFinite(north) ? north : DEFAULT_SITE.origin.north,
      rl: Number.isFinite(rl) ? rl : DEFAULT_SITE.origin.rl,
    },
    theme: {
      gold: theme.gold || DEFAULT_SITE.theme.gold,
      goldDim: theme.goldDim || DEFAULT_SITE.theme.goldDim,
      black: theme.black || DEFAULT_SITE.theme.black,
    },
    modules: mergeModules({ ...DEFAULT_SITE.modules, ...(raw.modules || {}) }),
    defaultLayers: Array.isArray(raw.defaultLayers) ? raw.defaultLayers : DEFAULT_SITE.defaultLayers,
    defaultCamera: raw.defaultCamera
      ? {
          position: raw.defaultCamera.position || DEFAULT_SITE.defaultCamera.position,
          lookAt: raw.defaultCamera.lookAt || DEFAULT_SITE.defaultCamera.lookAt,
        }
      : DEFAULT_SITE.defaultCamera,
    levelLabels: mergeLevelLabels(raw.levelLabels || DEFAULT_SITE.levelLabels),
    sections: mergeSections(raw.sections || DEFAULT_SITE.sections),
    holeProjects: mergeHoleProjects(raw.holeProjects),
    gradeBins: mergeGradeBins(raw.gradeBins),
  };
}

/** Apply theme CSS variables from site.json (scrollbar, body, var(--mdv-gold) usages). */
export function applySiteTheme(siteConfig) {
  const theme = siteConfig?.theme || DEFAULT_SITE.theme;
  const root = document.documentElement;
  if (theme.gold) root.style.setProperty('--mdv-gold', theme.gold);
  if (theme.goldDim) root.style.setProperty('--mdv-gold-dim', theme.goldDim);
  if (theme.black) root.style.setProperty('--mdv-black', theme.black);
}

export function getSite() {
  return site;
}

export function setSite(next) {
  site = mergeSite(next);
  return site;
}

export function getOrigin() {
  return site.origin;
}

export function getSiteModules() {
  const modules = site.modules || DEFAULT_SITE.modules;
  return {
    openPit: modules.openPit === true,
    underground: modules.underground === true,
  };
}

export function getLevelLabels() {
  return site.levelLabels || DEFAULT_SITE.levelLabels;
}

export function getGradeBins() {
  return site.gradeBins || DEFAULT_SITE.gradeBins;
}

export function getSectionPresets() {
  const sections = site.sections || emptySections();
  const fromLevels = (getLevelLabels().levels || []).map(([name, rl]) => ({ name, rl }));
  const benches = [...(sections.benches || [])];
  fromLevels.forEach((row) => {
    if (!benches.some((b) => Math.abs(b.rl - row.rl) < 0.05)) benches.push(row);
  });
  return {
    bounds: sections.bounds || emptySections().bounds,
    zones: sections.zones || [],
    benches,
    northings: sections.northings || [],
  };
}

/** Mine-coord slider limits. Falls back to origin ± a working envelope. */
export function getSectionBounds() {
  const presets = getSectionPresets();
  const o = getOrigin();
  return {
    east: presets.bounds?.east || [o.east - 2500, o.east + 2500],
    north: presets.bounds?.north || [o.north - 2500, o.north + 2500],
    rl: presets.bounds?.rl || [o.rl - 400, o.rl + 200],
  };
}

/** [east, north, rl] — SceneComponent / HolesLoader / AssayLoader */
export function getOriginENR() {
  const o = site.origin;
  return [o.east, o.north, o.rl];
}

/** [east, rl, north] — BlockModels / DatabaseHoleRender / HoleRender */
export function getOriginERL() {
  const o = site.origin;
  return [o.east, o.rl, o.north];
}

export async function loadSiteConfig() {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  try {
    const res = await fetch(`${base}/site.json`);
    if (res.ok) {
      const json = await res.json();
      setSite(json);
    }
  } catch (e) {
    console.warn('[site] using built-in defaults', e?.message || e);
  }
  applySiteTheme(site);
  if (typeof document !== 'undefined' && site.productName) {
    document.title = site.productName;
  }
  return site;
}
